/**
 * The bench press in the Kraftraum, as arithmetic: ten reps, a marker sweeping
 * a bar, a target zone that narrows every hit. Nothing here knows a pixel —
 * `markerPos01` and `zoneFor` answer in 0..1 — which is what makes it testable.
 *
 * The clock is a DURATION: `markerPos01` takes milliseconds since the session
 * started, so a press and the frame the player saw agree, and a backgrounded
 * tab lands the marker where the maths says. `gymScore` (0..1) goes to the
 * SERVER, which multiplies it into `trainingGain`, where a zero still pays half
 * a session — nothing here decides what a point is worth.
 */

/** How many presses make one session. Ten is a set: long enough that the zone
 *  visibly closes, short enough to be over before it is a chore. */
export const GYM_REPS = 10;

/**
 * One full sweep of the marker, there and back, ms. 650 ms across the bar: fast
 * enough to need attention, slow enough to aim. Difficulty is carried by the
 * ZONE shrinking, not the marker speeding up.
 */
export const GYM_PERIOD_MS = 1300;

/** The zone's width at the first rep and at the last. Linear in between. */
const ZONE_WIDE = 0.3;
const ZONE_TIGHT = 0.16;

/**
 * Where the marker is `tMs` into the session: a triangle wave, 1 at half a
 * period, 0 at the end. TOTAL — a negative or non-finite duration reads as the
 * start.
 */
export function markerPos01(tMs: number): number {
  if (!Number.isFinite(tMs)) return 0;
  const half = GYM_PERIOD_MS / 2;
  // The double modulo is what makes a negative duration wrap forwards rather
  // than mirroring the wave into negative positions.
  const phase = ((tMs % GYM_PERIOD_MS) + GYM_PERIOD_MS) % GYM_PERIOD_MS;
  return phase <= half ? phase / half : 2 - phase / half;
}

/** The band a press has to land in, in the marker's own 0..1 units. */
export interface GymZone {
  lo: number;
  hi: number;
}

/**
 * The target for rep `rep`, always centred and narrowing from 0.30 wide to 0.16
 * — the player learns ONE timing (the turnaround) and only the precision
 * changes. Out-of-range reps clamp.
 */
export function zoneFor(rep: number): GymZone {
  const last = GYM_REPS - 1;
  const clamped = !Number.isFinite(rep) || rep <= 0 ? 0 : rep >= last ? last : Math.floor(rep);
  const width = ZONE_WIDE + (ZONE_TIGHT - ZONE_WIDE) * (last > 0 ? clamped / last : 0);
  return { lo: 0.5 - width / 2, hi: 0.5 + width / 2 };
}

/**
 * One session in progress. `startedAtMs` is `performance.now()` and never
 * leaves the client; the SERVER stamps the cooldown.
 */
export interface GymState {
  /** How many reps are done — also the index of the zone being aimed at. */
  rep: number;
  hits: number;
  startedAtMs: number;
  /** What the last press was, for the flash. `null` before the first one. */
  lastResult: "hit" | "miss" | null;
  done: boolean;
}

export function startGym(nowMs: number): GymState {
  return { rep: 0, hits: 0, startedAtMs: nowMs, lastResult: null, done: false };
}

/**
 * One press at `nowMs`. NEVER MUTATES — the caller holds React state. A press
 * after the tenth answers with the same object.
 */
export function pressGym(s: GymState, nowMs: number): GymState {
  if (s.done) return s;

  const pos = markerPos01(nowMs - s.startedAtMs);
  const zone = zoneFor(s.rep);
  const hit = pos >= zone.lo && pos <= zone.hi;
  const rep = s.rep + 1;

  return {
    rep,
    hits: s.hits + (hit ? 1 : 0),
    startedAtMs: s.startedAtMs,
    lastResult: hit ? "hit" : "miss",
    done: rep >= GYM_REPS,
  };
}

/**
 * The session's result in 0..1 — measured against the whole set, so an
 * abandoned session is never worth more than a finished one.
 */
export function gymScore(s: GymState): number {
  return GYM_REPS > 0 ? s.hits / GYM_REPS : 0;
}
